// client/src/components/MerchantFidViewer.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  doc, onSnapshot, getDoc, getDocFromCache,
  collection, query, where
} from 'firebase/firestore';
import { firestore, storage } from '../../firebase';
import { FaArrowLeft, FaGift, FaShoppingCart, FaExchangeAlt } from 'react-icons/fa';
import { ref, getDownloadURL, listAll, getMetadata } from 'firebase/storage';
import MerchantNavbar from './MerchantNavbar';

// --- Logo avec cache localStorage + TTL 7j ---
const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const MerchantLogo = ({ merchantId }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!merchantId) return;
    const cacheKey = `merchantLogo_${merchantId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { url, ts } = JSON.parse(cached);
        if (url) setLogoUrl(url);
        if (ts && Date.now() - ts < LOGO_TTL_MS) return; // cache frais
      } catch {}
    }

    let mounted = true;
    (async () => {
      try {
        const logoRef = ref(storage, `${merchantId}/photo-profil/`);
        const logoList = await listAll(logoRef);
        if (logoList.items.length === 0) {
          localStorage.removeItem(cacheKey);
          if (mounted) setLogoUrl(null);
          return;
        }
        const withMeta = await Promise.all(
          logoList.items.map(async (item) => {
            const meta = await getMetadata(item);
            return { item, ts: meta.timeCreated ? new Date(meta.timeCreated).getTime() : 0 };
          })
        );
        withMeta.sort((a, b) => b.ts - a.ts);
        const { item } = withMeta[0];
        const freshUrl = await getDownloadURL(item);
        localStorage.setItem(cacheKey, JSON.stringify({ url: freshUrl, ts: Date.now() }));
        if (mounted) setLogoUrl(freshUrl);
      } catch (e) { console.error('Logo merchant:', e); }
    })();

    return () => { mounted = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      src={logoUrl}
      alt="Logo du commerçant"
      style={{
        width: 72, height: 72, objectFit: 'cover', borderRadius: '50%',
        display: 'block', margin: '0 auto 12px', border: '1px solid var(--eb-border)'
      }}
      loading="lazy"
    />
  ) : null;
};

// ------ FORMAT DATE COMPACT ------
const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

// Helpers activité
const toDate = (x) => (typeof x?.toDate === 'function' ? x.toDate() : new Date(x || 0));
const computeActivityTs = (data) => {
  if (!data) return 0;
  const arr = []
    .concat(data.timestamps || [])
    .concat(data.pointshistory || [])
    .concat(data.resetstamp || [])
    .concat(data.transferHistory || []);
  if (!arr.length) return 0;
  return arr.reduce((max, e) => {
    const t = e?.date ? toDate(e.date).getTime() : (Number(e?.at) || 0);
    return Math.max(max, t || 0);
  }, 0);
};

const MerchantFidViewer = () => {
  const { merchantId } = useParams();
  const navigate = useNavigate();

  const [fidelityData, setFidelityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [fidelityType, setFidelityType] = useState(null);
  const [pointsPaliers, setPointsPaliers] = useState([]);
  const [merchantPseudo, setMerchantPseudo] = useState('');

  // cache simple des pseudos pour les transferts
  const [uidToPseudo, setUidToPseudo] = useState({});
  const pseudoCacheRef = useRef({});

  // arbitre la source la plus fraîche sans lectures supplémentaires
  const lastActivityTsRef = useRef(0);
  const latestClientDataRef = useRef(null);
  const latestMerchantMetaRef = useRef(null);

  const applyMerge = useCallback((clientData) => {
    latestClientDataRef.current = {
      ...clientData,
      timestamps: clientData?.timestamps || [],
      resetstamp: clientData?.resetstamp || [],
      pointshistory: clientData?.pointshistory || [],
      transferHistory: clientData?.transferHistory || [],
    };
    setFidelityData({
      ...(latestMerchantMetaRef.current || {}),
      ...latestClientDataRef.current,
    });
  }, []);

  const maybeUpdateFrom = useCallback((incoming) => {
    const ts = computeActivityTs(incoming) || Date.now(); // prend au moins la première reçue
    if (ts >= lastActivityTsRef.current) {
      lastActivityTsRef.current = ts;
      applyMerge(incoming);
    }
  }, [applyMerge]);

  useEffect(() => {
    const auth = getAuth();

    let unsubAuth = null;
    let unsubMerchant = null;
    let unsubLegacyDoc = null;
    let unsubClientQuery = null;
    let unsubClientDoc = null;

    const cleanup = () => {
      if (unsubLegacyDoc) { unsubLegacyDoc(); unsubLegacyDoc = null; }
      if (unsubClientDoc) { unsubClientDoc(); unsubClientDoc = null; }
      if (unsubClientQuery) { unsubClientQuery(); unsubClientQuery = null; }
      if (unsubMerchant) { unsubMerchant(); unsubMerchant = null; }
      if (unsubAuth) { unsubAuth(); unsubAuth = null; }
    };

    // reset
    lastActivityTsRef.current = 0;
    latestClientDataRef.current = null;
    latestMerchantMetaRef.current = null;
    setFidelityData(null);
    setUidToPseudo({});
    pseudoCacheRef.current = {};
    setLoading(true);

    unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (unsubLegacyDoc) { unsubLegacyDoc(); unsubLegacyDoc = null; }
      if (unsubClientDoc) { unsubClientDoc(); unsubClientDoc = null; }
      if (unsubClientQuery) { unsubClientQuery(); unsubClientQuery = null; }
      if (unsubMerchant) { unsubMerchant(); unsubMerchant = null; }

      if (!currentUser) { setLoading(false); return; }
      const userUid = currentUser.uid;

      // pseudo marchand: cache d'abord
      try {
        const userRef = doc(firestore, 'users', merchantId);
        let userSnap = null;
        try { userSnap = await getDocFromCache(userRef); } catch {}
        if (!userSnap) userSnap = await getDoc(userRef);
        setMerchantPseudo(userSnap.exists() ? (userSnap.data()?.pseudo || 'Inconnu') : 'Inconnu');
      } catch { setMerchantPseudo('Inconnu'); }

      // méta marchand
      const merchantDocRef = doc(firestore, 'fidelity', merchantId);
      unsubMerchant = onSnapshot(
        merchantDocRef,
        (merchantSnap) => {
          if (!merchantSnap.exists()) { setLoading(false); return; }
          const merchantData = merchantSnap.data() || {};
          latestMerchantMetaRef.current = merchantData;

          const type = merchantData.Type || 'Fidbycount';
          setFidelityType(type);
          if (type === 'Fidbypoints') {
            const levels = merchantData.levels || [
              { points: merchantData.points1, gift: merchantData.gift1 },
              { points: merchantData.points2, gift: merchantData.gift2 },
              { points: merchantData.points3, gift: merchantData.gift3 },
            ].filter(l => l.points > 0);
            setPointsPaliers(levels);
          } else {
            setPointsPaliers([]);
          }

          // si on a déjà des données client, on fusionne sans nouvelle lecture
          if (latestClientDataRef.current) {
            setFidelityData({ ...merchantData, ...latestClientDataRef.current });
          }
          setLoading(false);
        },
        (err) => { console.error('Écoute merchant:', err); setLoading(false); }
      );

      // LEGACY: feedback immédiat, pas de reads supplémentaires
      const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', userUid);
      unsubLegacyDoc = onSnapshot(
        legacyRef,
        (snap) => {
          if (snap.exists()) {
            maybeUpdateFrom(snap.data() || {});
          }
          setLoading(false);
        },
        (err) => { console.error('Écoute legacy client:', err); setLoading(false); }
      );

      // CLIENT UID: écoute une query peu coûteuse, sans getDocs/getDoc
      const listColRef = collection(firestore, 'fidelity', merchantId, 'list');
      const qClient = query(listColRef, where('clientUid', '==', userUid));
      let currentPath = null;

      unsubClientQuery = onSnapshot(
        qClient,
        (qs) => {
          const first = !qs.empty ? qs.docs[0] : null;
          const nextPath = first ? first.ref.path : null;

          if (nextPath !== currentPath) {
            currentPath = nextPath;
            if (unsubClientDoc) { unsubClientDoc(); unsubClientDoc = null; }

            if (first) {
              unsubClientDoc = onSnapshot(
                first.ref,
                (snap) => {
                  if (snap.exists()) {
                    maybeUpdateFrom(snap.data() || {});
                  }
                },
                (err) => console.error('Écoute clientUid doc:', err)
              );
            }
          }
          setLoading(false);
        },
        (err) => { console.error('Query clientUid error:', err); setLoading(false); }
      );
    });

    return () => cleanup();
  }, [merchantId, maybeUpdateFrom]);

  // Résolution lazy des pseudos (mini-cache)
  useEffect(() => {
    const loadMissingPseudos = async () => {
      const arr = fidelityData?.transferHistory || [];
      if (!arr.length) return;
      const missing = new Set();
      arr.forEach((t) => {
        if (t?.direction === 'in' && t?.fromUid && !pseudoCacheRef.current[t.fromUid]) missing.add(t.fromUid);
        if (t?.direction === 'out' && t?.toUid && !pseudoCacheRef.current[t.toUid]) missing.add(t.toUid);
      });
      if (!missing.size) return;

      const entries = await Promise.all(
        Array.from(missing).map(async (uid) => {
          try {
            const snap = await getDoc(doc(firestore, 'users', uid));
            const pseudo = snap.exists() ? (snap.data()?.pseudo || 'Utilisateur') : 'Utilisateur';
            return [uid, pseudo];
          } catch {
            return [uid, 'Utilisateur'];
          }
        })
      );

      const next = { ...pseudoCacheRef.current };
      const add = {};
      entries.forEach(([uid, pseudo]) => { next[uid] = pseudo; add[uid] = pseudo; });
      pseudoCacheRef.current = next;
      setUidToPseudo((prev) => ({ ...prev, ...add }));
    };
    loadMissingPseudos();
  }, [fidelityData?.transferHistory]);

  // Historique aplati (inclut transferts)
  const flattenedHistory = useMemo(() => {
    if (!fidelityData) return [];

    const timestamps = (fidelityData.timestamps || []).map((ts, i) => ({
      id: `ts-${i}`, date: toDate(ts.date), type: 'timestamp', count: ts.count ?? 1,
    }));

    const resetstamps = (fidelityData.resetstamp || []).map((rs, i) => ({
      id: `rs-${i}`, date: toDate(rs.date), type: 'resetstamp', count: rs.count ?? 1, giftAtTime: rs.giftAtTime,
    }));

    const pointshistory = (fidelityData.pointshistory || []).map((ph, i) => ({
      id: `ph-${i}`, date: toDate(ph.date), type: 'pointshistory', points: ph.points, montant: ph.montant, gift: ph.gift,
    }));

    const transfers = (fidelityData.transferHistory || []).map((t, i) => ({
      id: `tr-${t.txId || i}`,
      date: new Date(Number(t.at || 0) || 0),
      type: 'transfer',
      direction: t.direction,
      kind: t.kind,
      qty: Number(t.qty || 0),
      fromUid: t.fromUid || null,
      toUid: t.toUid || null,
      txId: t.txId || null,
    }));

    return [...timestamps, ...resetstamps, ...pointshistory, ...transfers]
      .sort((a, b) => b.date - a.date);
  }, [fidelityData]);

  // UI "points à tampons"
  const renderFidelityPoints = (count, total, gift) => {
    const isNextReward = count === (total || 0) - 1;
    const finalColor = isNextReward ? '#E0A100' : 'var(--eb-brand)';
    const dots = [];
    for (let i = 1; i <= Math.max(1, (total || 1) - 1); i++) {
      dots.push(
        <div
          key={i}
          style={{
            width: 32, height: 32, borderRadius: '50%',
            backgroundColor: i <= (count || 0) ? finalColor : 'var(--eb-border)',
            color: i <= (count || 0) ? '#fff' : 'var(--eb-muted)', display: 'flex',
            justifyContent: 'center', alignItems: 'center',
            fontSize: 13, fontWeight: 600, flex: '0 0 auto',
            transition: 'background-color 0.3s ease-in-out'
          }}
        >
          {i}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ backgroundColor: isNextReward ? 'var(--eb-warning-bg)' : 'var(--eb-brand-50)', color: isNextReward ? 'var(--eb-warning)' : 'var(--eb-brand)', border: `1px solid ${isNextReward ? '#fde3b0' : 'var(--eb-brand-100)'}`, borderRadius: 'var(--eb-radius-pill)', padding: '8px 16px', fontWeight: 600, fontSize: 14, marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%', overflowWrap: 'anywhere' }}>
          <FaGift size={15} /><span>{gift}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 6 }}>
          {dots}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <MerchantNavbar pseudo={merchantPseudo} />

      {/* Styles */}
      <style>{`
        .mfv-scope { width: 100%; max-width: 860px; min-width: 0; }
        .mfv-scope .mfv-back { margin-bottom: 12px; padding-left: 8px; }
        .mfv-scope .mfv-card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 24px; text-align: center; margin-bottom: 24px; }
        .mfv-scope .mfv-name { margin: 0 0 16px; font-size: 22px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); text-align: center; }
        .mfv-scope .mfv-code { display: inline-flex; align-items: center; justify-content: center; padding: 10px 16px; border-radius: var(--eb-radius); border: 1.5px dashed var(--eb-border-strong); background: var(--eb-surface-2); font-family: var(--eb-font-mono); font-size: 18px; letter-spacing: 3px; color: var(--eb-text); margin-bottom: 20px; max-width: 100%; }
        .mfv-scope .mfv-panel { padding: 16px; border: 1px solid var(--eb-border); border-radius: var(--eb-radius); background: var(--eb-surface-2); }
        .mfv-scope .mfv-progress { margin: 16px 0 0; font-weight: 600; font-size: 14.5px; color: var(--eb-text-2); }
        .mfv-scope .mfv-next { margin: 8px 0 0; color: var(--eb-warning); font-weight: 600; font-size: 14.5px; }
        .mfv-scope .mfv-points { margin: 0 0 16px; padding: 14px 16px; border-radius: var(--eb-radius); background: var(--eb-brand-50); border: 1px solid var(--eb-brand-100); font-size: 18px; font-weight: 700; color: var(--eb-brand-800); }
        .mfv-scope .mfv-tier { padding: 12px 14px; margin: 8px 0; border-radius: var(--eb-radius-sm); text-align: left; border: 1px solid var(--eb-border); background: var(--eb-surface); }
        .mfv-scope .mfv-tier.is-reached { border-color: #fde3b0; background: var(--eb-warning-bg); }
        .mfv-scope .mfv-tier p { margin: 0; font-size: 14.5px; color: var(--eb-text); }
        .mfv-scope .mfv-tier .mfv-tier-pts { margin-top: 4px; font-size: 13px; font-weight: 600; color: var(--eb-muted); }
        .mfv-scope .mfv-section-title { margin: 0 0 12px; font-size: 17px; font-weight: 650; color: var(--eb-text); text-align: left; }
        .mfv-scope .mfv-empty { margin: 0; padding: 32px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); }

        .mfv-scope .table-fidelity-wrap { width: 100%; overflow-x: auto; }
        .mfv-scope .table-fidelity { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); overflow: hidden; box-shadow: var(--eb-shadow-sm); font-size: 14px; }
        .mfv-scope .table-fidelity thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid var(--eb-border); }
        .mfv-scope .table-fidelity th, .mfv-scope .table-fidelity td { padding: 12px 16px; text-align: left; vertical-align: middle; }
        .mfv-scope .table-fidelity tbody td { border-bottom: 1px solid var(--eb-border); color: var(--eb-text-2); }
        .mfv-scope .table-fidelity tbody tr:last-child td { border-bottom: 0; }
        .mfv-scope .table-fidelity tbody tr:hover { background: var(--eb-surface-2); }
        .mfv-scope .table-fidelity .td-date { color: var(--eb-muted); font-size: 13.5px; white-space: nowrap; }
        .mfv-scope .table-fidelity .td-type { font-weight: 600; color: var(--eb-text); }
        .mfv-scope .td-type .label { display: inline; }
        .mfv-scope .td-type .icon-only { display: none; }

        .mfv-scope .pill { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; padding: 3px 10px; border-radius: var(--eb-radius-pill); border: 1px solid transparent; font-size: 13px; white-space: nowrap; }
        .mfv-scope .pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
        .mfv-scope .pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
        .mfv-scope .pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
        .mfv-scope .pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }

        .mfv-scope .row-gift { background: #fffcf2; }
        .mfv-scope .row-in   { background: #f6fef9; }
        .mfv-scope .row-out  { background: #fffafa; }

        .mfv-scope .mfv-more { display: flex; justify-content: center; margin-top: 16px; }

        @media (max-width: 590px) {
          .mfv-scope .td-type .label { display: none; }
          .mfv-scope .td-type .icon-only { display: inline-flex; align-items: center; justify-content: center; width: 100%; font-size: 16px; }
        }
        @media (max-width: 768px) {
          .mfv-scope .mfv-card { padding: 20px 16px; }
          .mfv-scope .table-fidelity { table-layout: fixed; font-size: 13px; }
          .mfv-scope .table-fidelity th, .mfv-scope .table-fidelity td { padding: 10px 8px; }
          .mfv-scope .table-fidelity thead th { font-size: 11px; }
          .mfv-scope .table-fidelity .td-date { white-space: normal; font-size: 13px; }
          .mfv-scope .pill { padding: 3px 8px; font-size: 12.5px; }
        }
      `}</style>

      <div className="mfv-scope">
        <button
          onClick={() => navigate('/merchantfid')}
          className="eb-btn eb-btn--ghost eb-btn--sm mfv-back"
          title="Retour"
          aria-label="Retour"
        >
          <FaArrowLeft /> Retour
        </button>

      {/* Bloc carte/infos */}
        {loading ? (
          <div className="mfv-card" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <span className="eb-spinner" role="status" aria-label="Chargement" />
          </div>
        ) : fidelityData ? (
          <div className="mfv-card">
            <MerchantLogo merchantId={merchantId} />
            <h2 className="mfv-name">{merchantPseudo}</h2>

            {fidelityData && fidelityData.code && (
              <div>
                <span className="mfv-code">{fidelityData.code}</span>
              </div>
            )}

            {fidelityType === 'Fidbycount' && (
              <div className="mfv-panel">
                {renderFidelityPoints(fidelityData.compteur, fidelityData.multiple, fidelityData.gift)}
                <p className="mfv-progress">{fidelityData.compteur || 0} / {fidelityData.multiple || 0} passages effectués</p>
                {fidelityData.compteur === (fidelityData.multiple || 0) - 1 ? (
                  <p className="mfv-next">🎁 Gain au prochain passage : {fidelityData.gift} !</p>
                ) : null}
              </div>
            )}

            {fidelityType === 'Fidbypoints' && (
              <div>
                <p className="mfv-points">Vos points : {fidelityData.points || 0} pts</p>
                <div style={{ maxHeight: pointsPaliers.length > 3 ? '240px' : 'none', overflowY: pointsPaliers.length > 3 ? 'auto' : 'visible' }}>
                {pointsPaliers.map((palier, index) => (
                  palier.points > 0 && (
                    <div
                      key={index}
                      className={`mfv-tier${(fidelityData.points || 0) >= palier.points ? ' is-reached' : ''}`}
                    >
                      <p>{(fidelityData.points || 0) >= palier.points ? '🎁' : '⏳'}<strong> {palier.gift}</strong></p>
                      <p className="mfv-tier-pts">{palier.points} points</p>
                    </div>
                  )
                ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="mfv-empty" style={{ marginBottom: 24 }}>Aucune donnée trouvée.</p>
        )}

      {/* -------- HISTORIQUE -------- */}
      <div>
        <h3 className="mfv-section-title">Historique</h3>
        {(() => {
          const rows = flattenedHistory;
          if (!rows.length) return <p className="mfv-empty">Aucun historique des transactions.</p>;

          const allRows = rows;
          const visible = showAll ? allRows : allRows.slice(0, 5);

          return (
            <>
              <div className="table-fidelity-wrap">
                <table className="table-fidelity">
                  <thead>
                    <tr>
                      <th>{fidelityType === 'Fidbypoints' ? 'Points' : 'Scans'}</th>
                      <th>Date</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row, index) => {
                      const d = (row?.date instanceof Date && !isNaN(row.date)) ? row.date : new Date(0);
                      const formattedDate = formatCompactDateFR(d);

                      let typeLabel = '';
                      let typeIcon = null;
                      let value = '';
                      let rowStyle = {};
                      let valueClass = 'pill';
                      let rowClassName = '';

                      switch (row.type) {
                        case 'timestamp':
                          typeLabel = 'Scan 🛒';
                          typeIcon = <FaShoppingCart aria-hidden="true" />;
                          value = `+${row.count || 1}`;
                          valueClass += ' pill-scan';
                          break;

                        case 'resetstamp':
                          typeLabel = `${row.giftAtTime || 'Gain'} 🎁`;
                          typeIcon = <FaGift aria-hidden="true" />;
                          value = `x${row.count || 1}`;
                          valueClass += ' pill-gift';
                          rowStyle = { borderRadius: 6 };
                          rowClassName = 'row-gift';
                          break;

                        case 'pointshistory':
                          if (row.points > 0) {
                            typeLabel = 'Achat 🛒';
                            typeIcon = <FaShoppingCart aria-hidden="true" />;
                            value = `+${row.points} pts`;
                            valueClass += ' pill-pos';
                          } else {
                            typeLabel = `${row.gift || 'Gain'} 🎁`;
                            typeIcon = <FaGift aria-hidden="true" />;
                            value = `${row.points} pts`;
                            valueClass += ' pill-gift';
                            rowStyle = { borderRadius: 6 };
                            rowClassName = 'row-gift';
                          }
                          break;

                        case 'transfer': {
                          const unit = row.kind === 'passages' ? 'passages' : 'pts';
                          if (row.direction === 'in') {
                            const fromName = row.fromUid ? (uidToPseudo[row.fromUid] || 'Utilisateur') : 'Utilisateur';
                            typeLabel = `Transfert reçu de ${fromName} 🔁`;
                            value = `+${row.qty} ${unit}`;
                            valueClass += ' pill-pos';
                            rowClassName = 'row-in';
                          } else {
                            const toName = row.toUid ? (uidToPseudo[row.toUid] || 'Utilisateur') : 'Utilisateur';
                            typeLabel = `Transfert envoyé à ${toName} 🔁`;
                            value = `-${row.qty} ${unit}`;
                            valueClass += ' pill-neg';
                            rowClassName = 'row-out';
                          }
                          typeIcon = <FaExchangeAlt aria-hidden="true" />;
                          break;
                        }
                        default: break;
                      }

                      const uniqueKey = `row-${row.id}-${index}`;

                      return (
                        <tr key={uniqueKey} className={rowClassName} style={rowStyle}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span className={valueClass}>{value}</span>
                          </td>
                          <td className="td-date">{formattedDate}</td>
                          <td className="td-type" title={typeLabel} aria-label={typeLabel}>
                            <span className="label">{typeLabel}</span>
                            <span className="icon-only" style={{ marginLeft: 2 }}>{typeIcon}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {rows.length > 5 && (
                <div className="mfv-more">
                  <button
                    type="button"
                    className="show-more eb-btn eb-btn--secondary"
                    onClick={() => setShowAll((s) => !s)}
                  >
                    {showAll ? 'Afficher moins' : 'Afficher plus'}
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
      </div>
    </div>
  );
};

export default MerchantFidViewer;
